import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookUser, Download, RefreshCw, Search, Loader2, IdCard, Building2, User, AlertTriangle, BellRing } from 'lucide-react';
import FicheMaitreOuvrage, { ouvrirDocGed } from './FicheMaitreOuvrage';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { getToken } from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/registre-maitres-ouvrage`;
const h = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const Ligne = ({ f, onOuvrir, onRappel, rappelBusy }) => {
  const pro = f.type === 'professionnel';
  return (
    <tr onClick={() => onOuvrir(f.id)} className="cursor-pointer border-t border-slate-100 align-top hover:bg-slate-50/60" data-testid={`registre-mo-ligne-${f.id}`}>
      <td className="px-3 py-2.5">
        <p className="flex items-center gap-1.5 font-semibold text-slate-800">
          {pro ? <Building2 className="h-3.5 w-3.5 text-[#0a3d62]" /> : <User className="h-3.5 w-3.5 text-[#0a3d62]" />}
          {pro ? f.raison_sociale : `${f.nom || ''} ${f.prenom || ''}`}
        </p>
        <p className="text-xs text-slate-500">{pro ? `SIRET ${f.siret || '—'}${f.nom ? ` · ${f.nom} ${f.prenom || ''}` : ''}` : 'Particulier'}</p>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600">{[f.adresse, [f.code_postal, f.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}<br />{[f.telephone, f.email].filter(Boolean).join(' · ')}</td>
      <td className="px-3 py-2.5 text-xs text-slate-600">{(f.membres || []).map((m) => m.nom || m.id).join(', ') || '—'}</td>
      <td className="px-3 py-2.5 text-xs" title={(f.chantiers || []).map((c) => c.reference).join(', ')}>
        <span className="rounded-full bg-[#0a3d62]/10 px-2 py-0.5 font-semibold text-[#0a3d62]">{(f.chantiers || []).length}</span>
      </td>
      <td className="px-3 py-2.5">
        {(f.pieces_identite || []).length ? (f.pieces_identite.map((p) => (
          <button key={p.ged_id} type="button" onClick={(e) => { e.stopPropagation(); ouvrirDocGed(p.ged_id); }} className="mr-1 inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold capitalize text-emerald-700 hover:bg-emerald-100" data-testid={`registre-mo-piece-${p.ged_id}`}>
            <IdCard className="h-3 w-3" /> {p.face}
          </button>
        ))) : (
          <span className="flex flex-wrap items-center gap-1">
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid={`registre-mo-manquante-${f.id}`}><AlertTriangle className="h-3 w-3" /> Manquante</span>
            {onRappel && (
              <button type="button" onClick={(e) => { e.stopPropagation(); onRappel(f); }} disabled={rappelBusy} title="Envoyer un rappel au COOPER'S"
                className="inline-flex items-center gap-1 rounded-md border border-[#0a3d62]/30 px-2 py-0.5 text-xs font-semibold text-[#0a3d62] hover:bg-[#0a3d62]/5 disabled:opacity-50" data-testid={`registre-mo-rappel-${f.id}`}>
                {rappelBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Rappeler
              </button>
            )}
            {(f.rappels_piece || []).length > 0 && <span className="text-[10px] text-slate-400">rappel le {fmt(f.rappels_piece[f.rappels_piece.length - 1].at)}</span>}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500">{fmt(f.updated_at)}</td>
    </tr>
  );
};

/** Registre des maîtres d'ouvrage — super admin : tous ; COOPER'S : ceux de ses membres. */
export default function RegistreMaitresOuvrage() {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [sansPiece, setSansPiece] = useState(false);
  const [ficheId, setFicheId] = useState(null);
  const fermerFiche = useCallback(() => setFicheId(null), []);
  const charger = useCallback(async (recherche = '', filtre = false) => {
    setBusy('liste');
    try { setData((await axios.get(API, { ...h(), params: { q: recherche, sans_piece: filtre } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Registre indisponible'); }
    finally { setBusy(''); }
  }, []);
  useEffect(() => { charger(); }, [charger]);
  const rappeler = async (f) => {
    setBusy(`rappel-${f.id}`);
    try { const r = await axios.post(`${API}/${f.id}/rappel-piece`, {}, h()); toast.success(`Rappel envoyé à ${r.data.envoye_a.join(', ')}`); charger(q, sansPiece); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Rappel impossible'); } finally { setBusy(''); }
  };
  const basculer = () => { const v = !sansPiece; setSansPiece(v); charger(q, v); };
  const exporter = async (sansPieceParCooper = false) => {
    setBusy(sansPieceParCooper ? 'csv-sp' : 'csv');
    try {
      const r = await axios.get(`${API}/${sansPieceParCooper ? 'export-sans-piece.csv' : 'export.csv'}`, { ...h(), params: sansPieceParCooper ? {} : { q }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `${sansPieceParCooper ? 'maitres_ouvrage_sans_piece_par_cooper' : 'registre_maitres_ouvrage'}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    } catch { toast.error('Export impossible'); } finally { setBusy(''); }
  };
  const resync = async () => {
    setBusy('sync');
    try { const r = await axios.post(`${API}/resynchroniser`, {}, h()); toast.success(`${r.data.traites} enregistrement(s) repris`); charger(q, sansPiece); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Resynchronisation impossible'); } finally { setBusy(''); }
  };
  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="registre-mo">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-[#0a3d62]"><BookUser className="h-5 w-5" /> Registre des maîtres d'ouvrage</h2>
          <p className="text-xs text-slate-500">Alimenté automatiquement par les devis nominatifs et les souscriptions — une fiche par maître d'ouvrage.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === 'super_admin' && (
            <Button variant="outline" size="sm" onClick={resync} disabled={!!busy} data-testid="registre-mo-resync">
              {busy === 'sync' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Resynchroniser
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => exporter(true)} disabled={!!busy || !data?.sans_piece} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="registre-mo-export-sans-piece">
            {busy === 'csv-sp' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Sans pièce, par COOPER'S
          </Button>
          <Button size="sm" onClick={() => exporter()} disabled={!!busy || !data?.total} className="bg-[#0a3d62] hover:bg-[#0a3d62]/90" data-testid="registre-mo-export-csv">
            {busy === 'csv' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Exporter en CSV
          </Button>
        </div>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); charger(q, sansPiece); }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, raison sociale, SIRET, ville, référence de chantier…" className="pl-9" data-testid="registre-mo-recherche" />
        </div>
        <Button type="submit" variant="outline" data-testid="registre-mo-rechercher">Rechercher</Button>
      </form>
      {data?.sans_piece > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="registre-mo-alerte-pieces">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> {data.sans_piece} maître(s) d'ouvrage sans pièce d'identité</p>
          <Button size="sm" variant="outline" onClick={basculer} className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100" data-testid="registre-mo-filtre-sans-piece">
            {sansPiece ? 'Afficher tout' : 'Afficher seulement ceux-là'}
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-500" data-testid="registre-mo-total">{data ? `${data.total} maître(s) d'ouvrage` : 'Chargement…'}</p>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-3 py-2">Maître d'ouvrage</th><th className="px-3 py-2">Coordonnées</th><th className="px-3 py-2">Membres</th><th className="px-3 py-2">Chantiers</th><th className="px-3 py-2">Pièce d'identité</th><th className="px-3 py-2">Mis à jour</th></tr>
          </thead>
          <tbody>
            {busy === 'liste' && !data ? null : (data?.fiches || []).map((f) => <Ligne key={f.id} f={f} onOuvrir={setFicheId} rappelBusy={busy === `rappel-${f.id}`} onRappel={user?.role === 'super_admin' ? rappeler : null} />)}
            {data && !data.total && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400" data-testid="registre-mo-vide">Aucun maître d'ouvrage enregistré pour l'instant.</td></tr>}
          </tbody>
        </table>
      </div>
      <FicheMaitreOuvrage ficheId={ficheId} onClose={fermerFiche} onUpdate={() => charger(q, sansPiece)} />
    </section>
  );
}
